import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { mvpTableService } from '../../services/mvpTableService';

const AGE_GROUPS = [
  "Kinder",
  "Jugend", 
  "Erwachsene",
  "Senioren",
  "Senioren Plus"
];

// Beispieldaten für bessere Visualisierung
const sampleMVPData = [
  // Kinder
  { id: 1, name: "Max Mustermann", nation: "Deutschland", nationCode: "DE", ageGroup: "Kinder", totalPoints: 45, events: 3 },
  { id: 2, name: "Emma Schmidt", nation: "Deutschland", nationCode: "DE", ageGroup: "Kinder", totalPoints: 42, events: 3 },
  { id: 3, name: "Liam Johnson", nation: "USA", nationCode: "US", ageGroup: "Kinder", totalPoints: 38, events: 2 },
  { id: 4, name: "Sofia Chen", nation: "China", nationCode: "CN", ageGroup: "Kinder", totalPoints: 35, events: 3 },
  { id: 5, name: "Noah Kim", nation: "Südkorea", nationCode: "KR", ageGroup: "Kinder", totalPoints: 32, events: 2 },
  
  // Jugend
  { id: 6, name: "Anna Müller", nation: "Deutschland", nationCode: "DE", ageGroup: "Jugend", totalPoints: 52, events: 4 },
  { id: 7, name: "David Wang", nation: "China", nationCode: "CN", ageGroup: "Jugend", totalPoints: 48, events: 4 },
  { id: 8, name: "Sarah Johnson", nation: "USA", nationCode: "US", ageGroup: "Jugend", totalPoints: 45, events: 3 },
  { id: 9, name: "Tomoya Tanaka", nation: "Japan", nationCode: "JP", ageGroup: "Jugend", totalPoints: 41, events: 3 },
  { id: 10, name: "Marie Dubois", nation: "Frankreich", nationCode: "FR", ageGroup: "Jugend", totalPoints: 38, events: 2 },
  
  // Erwachsene
  { id: 11, name: "Michael Zhang", nation: "China", nationCode: "CN", ageGroup: "Erwachsene", totalPoints: 68, events: 5 },
  { id: 12, name: "Lisa Weber", nation: "Deutschland", nationCode: "DE", ageGroup: "Erwachsene", totalPoints: 62, events: 4 },
  { id: 13, name: "James Smith", nation: "USA", nationCode: "US", ageGroup: "Erwachsene", totalPoints: 58, events: 4 },
  { id: 14, name: "Yuki Sato", nation: "Japan", nationCode: "JP", ageGroup: "Erwachsene", totalPoints: 55, events: 4 },
  { id: 15, name: "Sophie Martin", nation: "Frankreich", nationCode: "FR", ageGroup: "Erwachsene", totalPoints: 51, events: 3 },
  { id: 16, name: "Marco Rossi", nation: "Italien", nationCode: "IT", ageGroup: "Erwachsene", totalPoints: 47, events: 3 },
  
  // Senioren
  { id: 17, name: "Hans Mueller", nation: "Deutschland", nationCode: "DE", ageGroup: "Senioren", totalPoints: 58, events: 4 },
  { id: 18, name: "Chen Wei", nation: "China", nationCode: "CN", ageGroup: "Senioren", totalPoints: 54, events: 4 },
  { id: 19, name: "Robert Brown", nation: "USA", nationCode: "US", ageGroup: "Senioren", totalPoints: 49, events: 3 },
  { id: 20, name: "Takeshi Yamamoto", nation: "Japan", nationCode: "JP", ageGroup: "Senioren", totalPoints: 45, events: 3 },
  { id: 21, name: "Pierre Dubois", nation: "Frankreich", nationCode: "FR", ageGroup: "Senioren", totalPoints: 41, events: 3 },
  
  // Senioren Plus
  { id: 22, name: "Klaus Fischer", nation: "Deutschland", nationCode: "DE", ageGroup: "Senioren Plus", totalPoints: 48, events: 3 },
  { id: 23, name: "Li Ming", nation: "China", nationCode: "CN", ageGroup: "Senioren Plus", totalPoints: 44, events: 3 },
  { id: 24, name: "John Wilson", nation: "USA", nationCode: "US", ageGroup: "Senioren Plus", totalPoints: 39, events: 2 },
  { id: 25, name: "Hiroshi Nakamura", nation: "Japan", nationCode: "JP", ageGroup: "Senioren Plus", totalPoints: 36, events: 2 },
  { id: 26, name: "Jean Moreau", nation: "Frankreich", nationCode: "FR", ageGroup: "Senioren Plus", totalPoints: 33, events: 2 }
];

// Hilfsfunktion zum Konvertieren der API-Daten in das erwartete Format
function convertMVPData(apiData) {
  const allAthletes = [];
  
  // Durchlaufe alle Altersgruppen und sammle alle Athleten
  Object.keys(apiData).forEach(ageGroup => {
    const groupAthletes = apiData[ageGroup];
    groupAthletes.forEach(athlete => {
      allAthletes.push({
        ...athlete,
        ageGroup: ageGroup
      });
    });
  });
  
  return allAthletes;
}

const MVPTableTailwind = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: "totalPoints", direction: "desc" });
  const [athletes, setAthletes] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMVPData();
  }, []);

  // Lade MVP-Daten von der API
  const loadMVPData = async () => {
    try {
      setLoading(true);
      const response = await mvpTableService.getMVPTable();
      
      // Prüfe ob response ein Objekt mit data-Property ist oder direkt die Daten
      const mvpData = response?.data || response;
      
      // Prüfe ob Daten vorhanden sind
      if (mvpData && typeof mvpData === 'object' && Object.keys(mvpData).length > 0) {
        const convertedData = convertMVPData(mvpData);
        if (convertedData && convertedData.length > 0) {
          setAthletes(convertedData);
        } else {
          // Keine Athleten in den Daten, setze leeres Array
          setAthletes([]);
        }
      } else {
        // Keine Daten vom Backend, setze leeres Array (kein Fallback zu Beispieldaten)
        setAthletes([]);
      }
    } catch (error) {
      console.error('Fehler beim Laden der MVP-Tabellen:', error);
      // Bei Fehler: leeres Array statt Fallback
      setAthletes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleAgeGroupChange = (ageGroup) => {
    setSelectedAgeGroup(ageGroup);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "desc" };
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  const toggleGroupExpansion = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  // Filter und Sortierung
  const filteredAthletes = athletes.filter((athlete) => {
    const matchesSearch = athlete.name.toLowerCase().includes(searchTerm.toLowerCase()) || athlete.nation.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAgeGroup = selectedAgeGroup === "all" || athlete.ageGroup === selectedAgeGroup;
    return matchesSearch && matchesAgeGroup;
  });

  // Gruppiere nach Altersgruppe
  const athletesByAgeGroup = AGE_GROUPS.reduce((acc, group) => {
    acc[group] = filteredAthletes.filter(a => a.ageGroup === group);
    return acc;
  }, {});

  // Sortiere pro Altersgruppe
  Object.keys(athletesByAgeGroup).forEach(group => {
    athletesByAgeGroup[group].sort((a, b) => {
      let cmp = 0;
      if (sortConfig.key === "totalPoints") {
        cmp = b.totalPoints - a.totalPoints;
      } else if (sortConfig.key === "name") {
        cmp = a.name.localeCompare(b.name);
      } else if (sortConfig.key === "nation") {
        cmp = a.nation.localeCompare(b.nation);
      } else if (sortConfig.key === "events") {
        cmp = b.events - a.events;
      }
      return sortConfig.direction === "asc" ? -cmp : cmp;
    });
  });

  // Funktion zum Rendern der Athleten für eine Gruppe
  const renderAthletesForGroup = (group) => {
    const groupAthletes = athletesByAgeGroup[group];
    const isExpanded = expandedGroups[group];
    const displayAthletes = isExpanded ? groupAthletes : groupAthletes.slice(0, 3);
    const hasMoreAthletes = groupAthletes.length > 3;

    return (
      <>
        {displayAthletes.map((athlete, idx) => (
          <tr key={athlete.id} className={idx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-700/30'}>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="flex items-center">
                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                  idx === 0 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                  idx === 1 ? 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300' :
                  idx === 2 ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300' :
                  'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                }`}>
                  {idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `#${idx+1}`}
                </span>
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-100">
              {athlete.name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="flex items-center">
                <img
                  src={process.env.PUBLIC_URL + `/images/flags/${athlete.nationCode}.png`}
                  alt={athlete.nation}
                  className="w-8 h-8 object-cover rounded-full border border-slate-200 dark:border-slate-600 mr-3"
                />
                <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{athlete.nation}</span>
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-center">
              <span className="inline-flex items-center justify-center w-12 h-8 bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-300 rounded-full text-sm font-bold">
                {athlete.totalPoints}
              </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-center">
              <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm font-bold">
                {athlete.events}
              </span>
            </td>
          </tr>
        ))}
        {hasMoreAthletes && (
          <tr className="bg-slate-50 dark:bg-slate-700/30">
            <td colSpan="5" className="px-6 py-4 text-center">
              <button
                className="inline-flex items-center px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                onClick={() => toggleGroupExpansion(group)}
              >
                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} mr-2`}></i>
                {isExpanded 
                  ? t("admin.mvpTable.showLess", "Weniger anzeigen") 
                  : t("admin.mvpTable.showMore", `Weitere ${groupAthletes.length - 3} Athleten anzeigen`)
                }
              </button>
            </td>
          </tr>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-64 mb-2"></div>
            <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-96"></div>
          </div>

          {/* Controls Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
              <div className="w-32 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-md"></div>
            </div>
          </div>

          {/* Age Group Cards Skeleton */}
          <div className="space-y-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 animate-pulse">
                <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-32 mb-4"></div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        {[...Array(5)].map((_, j) => (
                          <th key={j} className="px-4 py-2">
                            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-16"></div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...Array(5)].map((_, j) => (
                        <tr key={j}>
                          {[...Array(5)].map((_, k) => (
                            <td key={k} className="px-4 py-2">
                              <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                {t("admin.mvpTable.title", "MVPs je Altersgruppe")}
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-2">
                {t("admin.mvpTable.subtitle", "Die besten Athleten jeder Altersgruppe basierend auf der MVP-Punktzahl (Gruppengröße - Platzierung + 1).")}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{filteredAthletes.length}</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">{t("admin.mvpTable.athletes", "Athleten")}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder={t("admin.mvpTable.search", "Athlet oder Nation suchen...")}
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 h-[46px] border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
              />
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 text-sm"></i>
            </div>
          </div>
        </div>

        {/* Filter Groups */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
          <div className="space-y-6">
            {/* Age Group Filter */}
            <div>
              <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                {t("admin.mvpTable.ageGroup", "Altersgruppe")}
              </h3>
              <div className="flex flex-wrap gap-1.5">
                <button
                  className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    selectedAgeGroup === "all" ? 'bg-primary dark:bg-primary-light text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                  onClick={() => handleAgeGroupChange("all")}
                >
                  {t("admin.mvpTable.allAgeGroups", "Alle Altersgruppen")}
                </button>
                {AGE_GROUPS.map((group) => (
                  <button
                    key={group}
                    className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                      selectedAgeGroup === group ? 'bg-primary dark:bg-primary-light text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                    onClick={() => handleAgeGroupChange(group)}
                  >
                    {group}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* MVP Tables by Age Group */}
        {AGE_GROUPS.filter(group => selectedAgeGroup === "all" || selectedAgeGroup === group).map((group) => (
          <div key={group} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden mb-6 shadow-sm">
            {/* Group Header */}
            <div className="bg-slate-50 dark:bg-slate-700/50 px-6 py-4 border-b border-slate-200 dark:border-slate-600">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{group}</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {athletesByAgeGroup[group].length} Athleten in dieser Altersgruppe
              </p>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-16">
                      #
                    </th>
                    <th 
                      onClick={() => handleSort("name")} 
                      className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      {t("admin.mvpTable.name", "Name")} {getSortIcon("name")}
                    </th>
                    <th 
                      onClick={() => handleSort("nation")} 
                      className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      {t("admin.mvpTable.nation", "Nation")} {getSortIcon("nation")}
                    </th>
                    <th 
                      onClick={() => handleSort("totalPoints")} 
                      className="px-6 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      {t("admin.mvpTable.totalPoints", "MVP-Punkte")} {getSortIcon("totalPoints")}
                    </th>
                    <th 
                      onClick={() => handleSort("events")} 
                      className="px-6 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      {t("admin.mvpTable.events", "Events")} {getSortIcon("events")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                  {renderAthletesForGroup(group)}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </main>
  );
};

export default MVPTableTailwind;
